import { useCallback, useMemo } from 'react';

import {
  MembershipFormsFooter,
  MembershipUnitForms,
} from '#components/organisms/forms/MembershipForm.js';
import { type StoreItem, useShowConsumerStoreItem } from '@givebacks/api/store';
import { AdditionalFieldOption, Modal, createModalsFactory } from 'ui';

import {
  CheckoutCartOnOptions,
  SharedCheckoutButtonsProps,
} from '../../../molecules/Cart/buttons/CheckoutButtons/CheckoutButtons.types';

type SelectStoreItemModalIds = 'StoreItem' | 'AdditionalFields';

interface SelectStoreItemModalFlowMeta
  extends Pick<SharedCheckoutButtonsProps, 'cartFields' | 'quantity' | 'cartItems'> {
  storeId?: string;
  storeItemId?: string;
  checkoutCartAction?: 'add' | 'buy';
  storeItem?: StoreItem;
}

const {
  Provider: MembershipItemModalFlowProvider,
  useProvider: useMembershipItemModalFlowProvider,
  ModalTitleWithBackButton,
} = createModalsFactory<SelectStoreItemModalIds[], SelectStoreItemModalFlowMeta>([
  'StoreItem',
  'AdditionalFields',
]);

/**
 *
 * Provides the store item, additional fields, allowed years, and units to the children
 *  this allows this data to only be rendered after the modal has been opened
 * @returns
 */
const StoreItemWrapper = ({
  children,
  storeId,
  storeItemId,
}: {
  children: ({
    storeItem,
    additionalFields,
    allowedYears,
    units,
  }: {
    storeItem: StoreItem;
    additionalFields: AdditionalFieldOption[];
    allowedYears: { label: string; value: number }[];
    units: number;
  }) => React.ReactNode;
  storeId: string;
  storeItemId: string;
}) => {
  const { data: storeItem } = useShowConsumerStoreItem({ causeId: storeId, uuid: storeItemId });
  const additionalFields = useMemo(() => storeItem?.options ?? [], [storeItem?.options]);
  const allowedYears = useMemo(() => storeItem?.allowed_years ?? [], [storeItem?.allowed_years]);
  const units = useMemo(() => storeItem?.units ?? 1, [storeItem?.units]);
  if (!storeItem) return null;
  return children({ storeItem, additionalFields, allowedYears, units });
};

/**
 * Creates a form with additional field for the store item
 * This should only be used with one store item at a time
 */
const MembershipItemAdditionalFieldsModal = () => {
  const { opened, close, meta } = useMembershipItemModalFlowProvider({
    id: 'AdditionalFields',
  });

  const { checkoutCartAction, storeId, storeItemId, storeItem } = meta;

  const title = useMemo(() => {
    if (storeItem?.content.short_description) {
      return storeItem.content.short_description;
    }
    return checkoutCartAction == 'add' ? 'Add To Cart' : 'Buy Now';
  }, [checkoutCartAction, storeItem?.content.short_description]);

  const addToCartProps = useMemo(
    () => ({
      scopeId: storeId,
      buttonProps: {
        children: 'Add To Cart',
        variant: 'primary',
        size: 'xl',
      },
    }),
    [storeId]
  );

  if (!storeItem || !storeId || !storeItemId) return null;

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>{title}</ModalTitleWithBackButton>}
    >
      <StoreItemWrapper storeId={storeId} storeItemId={storeItemId}>
        {({ storeItem, additionalFields, allowedYears, units }) => (
          <>
            <MembershipUnitForms
              options={additionalFields}
              units={units}
              allowedYears={allowedYears}
            />
            <MembershipFormsFooter
              storeId={storeId}
              storeItem={storeItem}
              buttonProps={{
                addToCartProps,
                buyNowVisible: false,
              }}
              onSuccess={close}
            />
          </>
        )}
      </StoreItemWrapper>
    </Modal>
  );
};

interface StoreItemModalProps {
  opened: boolean;
  onClose: () => void;
  openModalById: (id: SelectStoreItemModalIds) => void;
  meta?: SelectStoreItemModalFlowMeta;
  openAdditionalFields: CheckoutCartOnOptions;
}

const MembershipItemModalWrapper = ({
  modal,
}: {
  modal?: (props: StoreItemModalProps) => React.ReactNode | React.ReactNode[];
}) => {
  const { opened, close, openModalById, setMeta } = useMembershipItemModalFlowProvider({
    id: 'StoreItem',
  });

  const handleOptionalFields: CheckoutCartOnOptions = useCallback(
    ({ quantity, checkoutCartAction, cartFields, cartItems }) => {
      setMeta((prev) => ({ ...prev, quantity, checkoutCartAction, cartFields, cartItems }));
      openModalById('AdditionalFields');
    },
    [openModalById, setMeta]
  );

  return modal?.({
    opened,
    onClose: close,
    openModalById,
    openAdditionalFields: handleOptionalFields,
  });
};

type MembershipItemModalProps = {
  storeId?: string;
  storeItemId?: string;
  storeItem?: StoreItem;
  children?: (props: StoreItemModalProps) => React.ReactNode | React.ReactNode[];
};

/**
 *
 * Used to provide a membership item with a dynamic modal flow where
 * the user can select additional fields before adding to cart
 * if the membership item requires additional fields
 *
 *  @important This component must only be used with one store item at a time, where cart_items contains only a single index
 *
 */
const MembershipItemModals = ({
  children,
  storeId,
  storeItemId,
  storeItem,
}: MembershipItemModalProps) => {
  return (
    <MembershipItemModalFlowProvider
      meta={{
        storeId,
        storeItemId,
        storeItem,
      }}
    >
      <MembershipItemModalWrapper modal={children} />
      <MembershipItemAdditionalFieldsModal />
    </MembershipItemModalFlowProvider>
  );
};

export { MembershipItemModals };
